'use es6';

import { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import autolinker from 'autolinker';
import { getLocalId } from 'conversations-internal-schema/file-upload/operators/fileUploadGetters';
import { isUploadCompleted } from 'conversations-internal-schema/file-upload/operators/isUploadCompleted';
import { DEFAULT_MAX_MESSAGE_LENGTH } from '../constants/defaultMaxMessageLength';
import I18n from 'I18n';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Raven from 'raven-js';
import MicrophoneButton from './MicrophoneButton';
import { usePostExternalApiEventOnMessageInputStateChanged } from '../hooks';
import FileUpload from '../../attachments/components/FileUpload';
import TextInput from './TextInput';
import AttachmentPickerButton from '../../attachments/components/AttachmentPickerButton';
import { isDesktopSafari } from '../../utils/isSafari';
import getAllowedFileTypes from '../../file-uploads/util/getAllowedFileTypes';
import { escape } from 'conversations-message-history/util/escape';
import { isMobileDevice } from 'conversations-visitor-experience-components/visitor-widget/util/isMobileDevice';
import { SendButton } from 'conversations-visitor-experience-components/message-editor/SendButton';
import MessageComposer from 'conversations-visitor-experience-components/message-editor/MessageComposer';
import { ScreenCaptureButton } from '../../screen-capture/ScreenCaptureButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FileUploadWrapper = styled.div.withConfig({
  displayName: "MessageEditor__FileUploadWrapper"
})(["display:flex;overflow:auto;& > *:not(:last-child){margin-right:8px;}"]);
let HAS_RAVEN_FIRED = false;
const COMPOSITION_ENTER_KEY_CODE = 229;
const MessageEditor = /*#__PURE__*/forwardRef(({
  canUploadAttachments,
  chatInputChanged,
  coloring: {
    accentColor,
    useDefaultColor
  } = {},
  disablePublish = false,
  disabled,
  fileTypes = getAllowedFileTypes(),
  inputStaging,
  maxAttachmentFileSize,
  mobile,
  isIOSMobile,
  networkOffline,
  onAttachmentButtonClick,
  onRemoveAttachment = () => {},
  onSelectAttachmentFiles = () => {},
  onSetMessageTextExternally = () => {},
  publishMessage,
  shouldDisableInitialInputFocus,
  shouldHideAttachmentButton,
  shouldSendInputStagingEvent,
  enableAIDisclaimer,
  showQuickReplyPlaceholder,
  showingHsBranding,
  spamProtectionEnabled,
  speechPocActive = false,
  speechRecordingStatus = false,
  stagedAttachments = List(),
  stagedMessageEditorText,
  supportsAttachments = false,
  toggleOnAndOffInputFocus = () => {},
  isUngatedForScreenCapture,
  isUngatedForCustomerAgentPrompts
}, ref) => {
  const [isComposing, setIsComposing] = useState(false);
  const [messageText, setMessageText] = useState(stagedMessageEditorText || '');
  useEffect(() => {
    if (stagedMessageEditorText) {
      setMessageText(prev => prev || stagedMessageEditorText);
    }
  }, [stagedMessageEditorText]);
  const _ref = useRef();
  const editorRef = ref || _ref;
  const focusEditor = () => {
    if (editorRef !== null && editorRef !== void 0 && editorRef.current) {
      editorRef.current.focus();
    }
  };
  useEffect(() => {
    // this does the same as focusEditor, not using function so that we can have exhaustive deps
    if (!disabled && !(shouldDisableInitialInputFocus || isMobileDevice()) && editorRef !== null && editorRef !== void 0 && editorRef.current) {
      editorRef.current.focus();
    }
  }, [editorRef, disabled, shouldDisableInitialInputFocus]);
  const getMessageText = () => {
    return messageText;
  };
  const messageTextIsWithinLimit = () => {
    return getMessageText().length < DEFAULT_MAX_MESSAGE_LENGTH;
  };
  const hasAttachments = () => {
    return stagedAttachments && stagedAttachments.size;
  };
  const attachmentsAreValid = () => {
    if (!stagedAttachments) {
      return true;
    }
    return stagedAttachments.every(isUploadCompleted);
  };
  const messageContentsAreValid = () => {
    const _messageText = getMessageText();
    const hasTextContent = typeof _messageText === 'string' && _messageText.trim() !== '';
    return messageTextIsWithinLimit() && attachmentsAreValid() && (hasTextContent || hasAttachments());
  };
  const canPublish = () => {
    return messageContentsAreValid() && !networkOffline && !disabled && !disablePublish;
  };
  const handleChange = useCallback(e => {
    const currentMessageText = e.target.value;
    setMessageText(currentMessageText);
    if (shouldSendInputStagingEvent && !currentMessageText) {
      inputStaging(currentMessageText, true);
    }
    chatInputChanged(currentMessageText);

    // https://issues.hubspotcentral.com/browse/CT-6256
    if ((disabled || disablePublish) && !HAS_RAVEN_FIRED) {
      Raven.captureMessage('VISITOR_CAN_TYPE_WHILE_PUBLISH_DISABLED');
      HAS_RAVEN_FIRED = true;
    }
  }, [shouldSendInputStagingEvent, inputStaging, chatInputChanged, disabled, disablePublish]);
  onSetMessageTextExternally(useCallback(text => {
    if (text) {
      handleChange({
        target: {
          value: text
        }
      });
    }
  }, [handleChange]));
  const handlePublish = () => {
    if (canPublish()) {
      const _messageText = getMessageText();
      const richText = `<div>${autolinker.link(escape(_messageText), {
        stripPrefix: false
      })}</div>`.replace(/\n/g, '<br>');
      publishMessage({
        text: _messageText,
        richText
      });
      setMessageText('');
      inputStaging('', true);
      focusEditor();
    }
  };

  /**
   * @param {KeyboardEvent} e
   */
  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.shiftKey === false && !isComposing) {
      // Checking deprecated property keyCode for value of 229 to prevent
      // Safari bug that publishes while still composing for Japanese input
      // https://bugs.webkit.org/show_bug.cgi?id=165004
      if (isDesktopSafari() && e.keyCode === COMPOSITION_ENTER_KEY_CODE) {
        return true;
      }
      handlePublish();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };
  const getValidationMessage = () => {
    return !messageTextIsWithinLimit() ? I18n.text('conversations-visitor-ui.chatTextArea.validationMessage') : '';
  };
  const handleSelectAttachmentFiles = ({
    files
  }) => {
    onSelectAttachmentFiles({
      files
    });
    focusEditor();
  };
  const onInputFocus = () => {
    if (!disabled) toggleOnAndOffInputFocus(true);
  };
  const onInputBlur = () => {
    toggleOnAndOffInputFocus(false);
  };
  const handlePaste = e => {
    var _e$clipboardData;
    if (e !== null && e !== void 0 && (_e$clipboardData = e.clipboardData) !== null && _e$clipboardData !== void 0 && _e$clipboardData.files && canUploadAttachments) {
      onSelectAttachmentFiles({
        files: e.clipboardData.files
      });
    }
  };
  const isTextInputDisabled = disabled || networkOffline || disablePublish;
  const TextInputComponent = /*#__PURE__*/_jsx(TextInput, {
    disableInitialInputFocus: shouldDisableInitialInputFocus,
    disabled: isTextInputDisabled,
    error: !messageTextIsWithinLimit(),
    messageText: messageText,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    showQuickReplyPlaceholder: showQuickReplyPlaceholder,
    ref: editorRef,
    showingHsBranding: showingHsBranding,
    validationMessage: getValidationMessage(),
    isIOSMobile: isIOSMobile,
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur,
    onPaste: handlePaste,
    onCompositionStart: () => setIsComposing(true),
    onCompositionEnd: () => setIsComposing(false)
  });
  const FileUploads = hasAttachments() ? /*#__PURE__*/_jsx(FileUploadWrapper, {
    className: 'm-bottom-2' + (showingHsBranding ? " m-top-2" : ""),
    children: stagedAttachments.map(attachment => {
      return /*#__PURE__*/_jsx(FileUpload, {
        attachment: attachment,
        onRemove: () => {
          onRemoveAttachment({
            attachment
          });
        }
      }, getLocalId(attachment));
    })
  }) : null;
  const color = !useDefaultColor ? accentColor : null;
  const isDisabled = !canPublish();
  usePostExternalApiEventOnMessageInputStateChanged({
    shouldSendEvent: shouldSendInputStagingEvent,
    disabled: isTextInputDisabled
  });
  return /*#__PURE__*/_jsx(MessageComposer, {
    inputToRender: TextInputComponent,
    FileUploads: FileUploads,
    showingHsBranding: showingHsBranding,
    accentColor: isUngatedForCustomerAgentPrompts ? accentColor : undefined,
    enableAIDisclaimer: enableAIDisclaimer,
    spamProtectionEnabled: spamProtectionEnabled,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MicrophoneButton, {
        disabled: disabled,
        speechRecordingStatus: speechRecordingStatus,
        speechPocActive: speechPocActive,
        mobile: mobile
      }), supportsAttachments && !shouldHideAttachmentButton ? /*#__PURE__*/_jsxs("div", {
        className: mobile ? 'm-right-3' : "",
        children: [isUngatedForScreenCapture && /*#__PURE__*/_jsx(ScreenCaptureButton, {
          disabled: !canUploadAttachments || disabled,
          mobile: mobile
        }), /*#__PURE__*/_jsx(AttachmentPickerButton, {
          accept: fileTypes,
          color: color,
          disabled: !canUploadAttachments || disabled,
          mobile: mobile,
          onClick: onAttachmentButtonClick,
          onSelectFiles: handleSelectAttachmentFiles,
          maxAttachmentFileSize: maxAttachmentFileSize
        })]
      }) : null, /*#__PURE__*/_jsx(SendButton, {
        disabled: isDisabled,
        onClick: handlePublish,
        iconColor: isUngatedForCustomerAgentPrompts ? accentColor : undefined
      })]
    })
  });
});
MessageEditor.propTypes = {
  canUploadAttachments: PropTypes.bool.isRequired,
  chatInputChanged: PropTypes.func.isRequired,
  coloring: PropTypes.object,
  disablePublish: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  enableAIDisclaimer: PropTypes.bool.isRequired,
  fileTypes: PropTypes.arrayOf(PropTypes.string),
  inputStaging: PropTypes.func.isRequired,
  isIOSMobile: PropTypes.bool,
  isUngatedForCustomerAgentPrompts: PropTypes.bool.isRequired,
  isUngatedForScreenCapture: PropTypes.bool.isRequired,
  maxAttachmentFileSize: PropTypes.number.isRequired,
  mobile: PropTypes.bool,
  networkOffline: PropTypes.bool.isRequired,
  onAttachmentButtonClick: PropTypes.func,
  onRemoveAttachment: PropTypes.func,
  onSelectAttachmentFiles: PropTypes.func,
  onSetMessageTextExternally: PropTypes.func,
  publishMessage: PropTypes.func.isRequired,
  shouldDisableInitialInputFocus: PropTypes.bool,
  shouldHideAttachmentButton: PropTypes.bool,
  shouldSendInputStagingEvent: PropTypes.bool.isRequired,
  showQuickReplyPlaceholder: PropTypes.bool.isRequired,
  showingHsBranding: PropTypes.bool,
  spamProtectionEnabled: PropTypes.bool.isRequired,
  speechPocActive: PropTypes.bool,
  speechRecordingStatus: PropTypes.string,
  stagedAttachments: PropTypes.instanceOf(List),
  stagedMessageEditorText: PropTypes.string,
  supportsAttachments: PropTypes.bool,
  toggleOnAndOffInputFocus: PropTypes.func
};
MessageEditor.displayName = 'MessageEditor';
export default MessageEditor;